import { useMemo, useState } from 'react'
import { scaleLinear } from '@visx/scale'
import type { ChartCanvasProps, SubScoreBarsData } from './chartTypes'
import { chartColor } from './visxPrimitives'
import { TooltipCard } from './TooltipCard'

type Active = { label: string; value: number; detail?: string; x: number; y: number } | null

export const SubScoreBarsChart = ({ data, width, height }: ChartCanvasProps<SubScoreBarsData>) => {
  const [active, setActive] = useState<Active>(null)
  const rowHeight = Math.max(28, Math.min(48, (height - 12) / Math.max(1, data.bars.length)))
  const labelWidth = 108
  const barWidth = Math.max(1, width - labelWidth - 42)
  const xScale = useMemo(() => scaleLinear({ domain: [0, 100], range: [0, barWidth] }), [barWidth])

  return (
    <div className="analysisChartCanvas">
      <svg className="analysisChartSvg" viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Health sub-scores">
        <title>Health sub-scores</title>
        {data.bars.map((bar, index) => {
          const y = index * rowHeight + 6
          const filled = xScale(bar.value)
          const barY = y + 6
          const accent = chartColor(index)
          const isActive = active?.label === bar.label
          return (
            <g key={bar.label}>
              <text className="analysisChartAxisText" x="0" y={y + rowHeight / 2 + 4}>{bar.label}</text>
              <rect x={labelWidth} y={barY} width={barWidth} height="12" rx="6" className="analysisChartTrack" />
              <rect
                x={labelWidth}
                y={barY}
                width={filled}
                height="12"
                rx="6"
                fill={accent}
                fillOpacity={isActive ? 0.95 : 0.82}
                className="analysisChartBar"
                tabIndex={0}
                role="button"
                aria-label={`${bar.label}: ${Math.round(bar.value)}%${bar.detail ? `. ${bar.detail}` : ''}`}
                onPointerEnter={() => setActive({ label: bar.label, value: bar.value, detail: bar.detail, x: labelWidth + filled, y: barY + 6 })}
                onPointerLeave={() => setActive(null)}
                onFocus={() => setActive({ label: bar.label, value: bar.value, detail: bar.detail, x: labelWidth + filled, y: barY + 6 })}
                onBlur={() => setActive(null)}
              />
              <text className="analysisChartAxisText" x={width - 8} y={y + rowHeight / 2 + 4} textAnchor="end">{Math.round(bar.value)}%</text>
            </g>
          )
        })}
      </svg>
      {active && (
        <TooltipCard
          left={active.x}
          top={active.y}
          title={active.label}
          value={`${Math.round(active.value)}%`}
          secondary={active.detail}
        />
      )}
    </div>
  )
}
